import type {RefCallback} from 'react'
import {useCallback, useState} from 'react'
import {useResizeObserver} from './useResizeObserver'

/**
 * Measures a ref'd element's live height (ResizeObserver) and returns baseOffset + that
 * height -- for handing to BasicDataGrid's `height` prop (a number resolves to
 * `calc(100vh - Npx)`), so the grid always fills exactly the remaining space below a
 * variable-height header. No magic per-breakpoint constants to keep in sync by hand, and
 * robust to window resize, zoom, and the header reflowing to more/fewer lines on its own
 * (a font load, a wrapping filter row gaining a row, etc) -- a plain window-resize listener
 * only catches viewport changes, not the header's own size changing independently of that.
 *
 * Same mechanism AlpinistTableIntro.tsx already used (for a table's intro-text blurb);
 * this is the generic version for any variable-height region above a table -- a filter
 * row, a banner, anything.
 */
export const useMeasuredHeightOffset = (baseOffset: number): [RefCallback<Element>, number] => {
  const [measuredHeight, setMeasuredHeight] = useState(0)
  const handleResize = useCallback((entry: ResizeObserverEntry): void => {
    setMeasuredHeight((entry.target as HTMLElement).offsetHeight)
  }, [])
  const ref = useResizeObserver(handleResize)
  return [ref, baseOffset + measuredHeight]
}
